import { Utils } from '@pacem/pacem-core';
import { Colors } from '@pacem/pacem-foundation';
import { Geometry } from '@pacem/pacem-numerical';
import { StalePropertyFlag, isGroup, isMesh, isCamera, isLight, isGeometry, isInteraction, isMeshGeometry } from '../../types';
import { KnownShader, isMaterial } from '../../materials/material';
import { AdapterUtils } from '../shared';
import { U32_MAX, LABEL_PREFIX } from './constants';
import { Buffers } from './buffers';
import { Cameras } from './cameras';
import { Materials } from './materials';
//namespace Pacem.Drawing3D.WebGPU {
const Vector3D = Geometry.LinearAlgebra.Vector3D;
const Matrix3D = Geometry.LinearAlgebra.Matrix3D;
function throwBufferRequestError(key, type) {
    if (typeof type === 'function') {
        type = type.name;
    }
    throw new RangeError(`Buffer '${key}' not available for ${type}.`);
}
function createRenderableBuffer(item, parent, ...args) {
    item.stage ??= parent?.item.stage;
    if (isGroup(item)) {
        item.key ??= 'group_' + Utils.uniqueCode();
        return new RenderableGroupBuffer(item, parent, ...args);
    }
    if (isMesh(item)) {
        item.key ??= 'mesh_' + Utils.uniqueCode();
        return new RenderableMeshBuffer(item, parent, ...args);
    }
    if (isCamera(item)) {
        item.key ??= 'camera_' + Utils.uniqueCode();
        return new RenderableCameraBuffer(item /*, parent*/);
    }
    if (isLight(item)) {
        item.key ??= 'light_' + item.type + '_' + Utils.uniqueCode();
        return new RenderableLightBuffer(item /*, parent*/);
    }
    throw new Error(`Renderable is not supported.`);
}
export class RenderLooper {
    constructor(_set) {
        this._set = _set;
    }
    /** @internal */
    get items() {
        return this._set;
    }
    loop(render) {
        const { meshes, cameras, groups, lights } = this._set;
        const camera = cameras[0];
        let cameraDone = false, lightsDone = false;
        for (let mesh of meshes) {
            if (mesh.disposed) {
                continue;
            }
            // execute
            const rendered = render(mesh, camera, lights);
            if (!rendered) {
                continue;
            }
            // seal buffers
            RenderableBuffers.clearFlags(mesh);
            if (!cameraDone) {
                RenderableBuffers.clearFlags(camera);
                cameraDone = true;
            }
            if (!lightsDone) {
                lights.forEach(light => RenderableBuffers.clearFlags(light));
                lightsDone = true;
            }
        }
        for (let group of groups) {
            // seal group buffers
            RenderableBuffers.clearFlags(group);
        }
    }
}
export class ManagedBuffers {
    static create(item) {
        if (isGeometry(item)) {
            item.key ??= 'geometry_' + Utils.uniqueCode();
            return new GeometryBuffer(item);
        }
        if (isMaterial(item)) {
            return new MaterialBuffer(item);
        }
        if (isInteraction(item)) {
            return new InteractionBuffer(item);
        }
        throw new Error(`Object is not supported.`);
    }
}
export class RenderableBuffers {
    static create(item, ...args) {
        return createRenderableBuffer(item, null, ...args);
    }
    static clearFlags(buffer, includeParent = false) {
        buffer.item.flags.splice(0);
        if (includeParent) {
            const bufferBase = buffer;
            if (!Utils.isNull(bufferBase.parent)) {
                RenderableBuffers.clearFlags(bufferBase.parent, true);
            }
        }
    }
    static allFlags(buffer) {
        const bufferBase = buffer;
        const parentBufferBase = bufferBase.parent;
        const parentFlags = Utils.isNull(parentBufferBase) ? [] : RenderableBuffers.allFlags(parentBufferBase);
        return bufferBase.item.flags.union(parentFlags);
    }
}
class ManagedBufferBase {
    #item;
    constructor(item) {
        this.#item = item;
    }
    get item() {
        return this.#item;
    }
    #lock = false;
    lock() {
        this.#lock = true;
    }
    unlock() {
        this.#lock = false;
        const garbage = this.#garbage;
        while (garbage.length > 0) {
            const obj = garbage.shift();
            this._destroyUnsafe(obj);
        }
    }
    #garbage = [];
    _destroyUnsafe(obj) {
        if ('texture' in obj) {
            obj.texture.destroy();
        }
        else if ('buffer' in obj) {
            obj.buffer.destroy();
        }
    }
    destroySafely(...objects) {
        for (let obj of Array.from(objects).concat(...this.#garbage.splice(0))) {
            if (!obj) {
                continue;
            }
            if (this.#lock) {
                this.#garbage.push(obj);
            }
            else {
                this._destroyUnsafe(obj);
            }
        }
    }
    #disposed = false;
    destroy() {
        this.dispose();
        this.#disposed = true;
    }
    get disposed() {
        return this.#disposed;
    }
}
class RenderableBufferBase extends ManagedBufferBase {
    constructor(item, parentBuffer) {
        super(item);
        this.#parent = parentBuffer;
        this.#u32id = Utils.uniqueId() % U32_MAX;
    }
    #u32id;
    #parent;
    #u32idBuffer = new Map();
    get parent() {
        return this.#parent;
    }
    get id() {
        return this.#u32id;
    }
    allFlags() {
        const parent = this.parent;
        // TODO: this one line is a perf hog!
        return this.item.flags.union(parent?.allFlags() ?? []);
    }
    buildParentWorldTransform() {
        let worldMatrix = Matrix3D.identity();
        let step = this;
        while (!Utils.isNull(step.parent)) {
            const parentItem = step.parent.item;
            const parentItemMatrix = Matrix3D.translate(parentItem.transformMatrix ?? Matrix3D.identity(), parentItem.position ?? Vector3D.zero());
            worldMatrix = Matrix3D.multiply(worldMatrix, parentItemMatrix);
            step = step.parent;
        }
        return worldMatrix;
    }
    buildWorldTransform() {
        const itemMatrix = Matrix3D.translate(this.item.transformMatrix ?? Matrix3D.identity(), this.item.position ?? Vector3D.zero());
        const parentWorldMatrix = this.buildParentWorldTransform();
        return Matrix3D.multiply(itemMatrix, parentWorldMatrix);
    }
    #versions = new Map();
    version(key) {
        return this.#versions.get(key) ?? 0;
    }
    buffer(context, key) {
        if (key === 'u32id') {
            const u32idBuffer = this.#u32idBuffer;
            const { pipeline, device } = context;
            if (u32idBuffer.has(pipeline)) {
                return Utils.extend({ fresh: false }, u32idBuffer.get(pipeline));
            }
            const u32idBufferValue = new Uint32Array([this.id]);
            const u32idBufferBuffer = Buffers.createUniform(device, u32idBufferValue, LABEL_PREFIX + 'u32id-buffer');
            const retval = Utils.extend({ buffer: u32idBufferBuffer, value: u32idBufferValue, version: performance.now() }, { fresh: true });
            this.#u32idBuffer.set(pipeline, retval);
            return retval;
        }
        const retval = this.specificBuffer(context, key);
        this.#versions.set(key, retval.version);
        return retval;
    }
}
const DefaultMaterial = { shader: KnownShader.Basic, color: '#c0c0c0', flags: [] };
// #region GROUP
class RenderableGroupBuffer extends RenderableBufferBase {
    #children;
    constructor(group, parent, ...args) {
        super(group, parent);
        this.#children = group.childRenderables.map(i => createRenderableBuffer(i, this, ...args));
    }
    get children() {
        return this.#children;
    }
    specificBuffer(ctx, key) {
        return null;
    }
    texture(_) {
        throw new Error("Method not implemented.");
    }
    dispose() {
        this.#children.forEach(i => i.destroy());
        //this.#children = null;
    }
}
// #endregion
// #region CAMERA
class RenderableCameraBuffer extends RenderableBufferBase {
    #viewBag = new Map();
    #view;
    constructor(camera) {
        super(camera);
    }
    texture(_) {
        throw new Error("Method not implemented.");
    }
    specificBuffer(context, key) {
        //const camera = this.item;
        let fresh = false;
        if (this.allFlags().length > 0 || Utils.isNull(this.#view)) {
            fresh = true;
            this.#viewBag.clear();
            this._rebuildMatrices(context);
        }
        const { pipeline } = context;
        if (!this.#viewBag.has(pipeline)) {
            fresh = true;
            this.#viewBag.set(pipeline, this.#view);
        }
        RenderableBuffers.clearFlags(this);
        switch (key) {
            case 'cameraViewTransform':
                return Utils.extend(this.#view, { fresh });
            default:
                throwBufferRequestError(key, RenderableCameraBuffer);
        }
    }
    _rebuildMatrices({ device, canvas }) {
        // this._destroyView();
        const cam = this.item;
        const camera = Cameras.create(cam);
        const { x, y, z } = cam.position;
        const view = camera.view();
        const projection = camera.projection(canvas);
        const viewProjection = Matrix3D.multiply(view, projection);
        const viewValue = new Float32Array(16 + 16 + 4);
        viewValue.set(Matrix3D.toArray(view), 0);
        viewValue.set(Matrix3D.toArray(viewProjection), 16);
        viewValue.set([x, y, z], 32);
        // reuse GPUBuffer (always same size)
        if (Utils.isNull(this.#view)) {
            const viewBuffer = Buffers.createUniform(device, viewValue, LABEL_PREFIX + 'view-buffer');
            this.#view = { buffer: viewBuffer, value: viewValue, version: performance.now().roundoff() };
        }
        else {
            this.#view.value = viewValue;
            this.#view.version = performance.now().roundoff();
        }
    }
    _destroyView() {
        this.#viewBag.clear();
        const view = this.#view;
        this.#view = null;
        this.destroySafely(view);
    }
    dispose() {
        this._destroyView();
    }
}
// #endregion
// #region GEOMETRY
class GeometryBuffer extends ManagedBufferBase {
    #bufferBag = {};
    #freshness = { positions: true, normals: true, uv: true, packed: true };
    #geometryBuffers;
    constructor(geometry) {
        super(geometry);
        geometry.flags ??= [];
    }
    buffer(context, key) {
        const flags = this.item.flags ?? [];
        if (Utils.isNull(this.#geometryBuffers) || flags.includes(StalePropertyFlag.Geometry)) {
            flags.splice(0);
            this.#bufferBag = /* reset */ {};
            this._rebuildGeometryBuffers(context);
        }
        const freshness = this.#freshness;
        const geom = this.#geometryBuffers;
        const { pipeline } = context;
        const bag = (this.#bufferBag[key] ??= new Map());
        const newInPipeline = !bag.has(pipeline);
        const ensureInBag = (buffer) => {
            if (newInPipeline) {
                bag.set(pipeline, buffer);
            }
            return buffer;
        };
        switch (key) {
            case 'geometryPositions':
                const { positions } = geom, posFresh = freshness.positions;
                freshness.positions = false;
                return Utils.extend(ensureInBag(positions), { fresh: posFresh || newInPipeline });
            case 'geometryNormals':
                const { normals } = geom, normFresh = freshness.normals;
                freshness.normals = false;
                return Utils.extend(ensureInBag(normals), { fresh: normFresh || newInPipeline });
            case 'geometryTexCoords':
                const { uv } = geom, uvFresh = freshness.uv;
                freshness.uv = false;
                return Utils.extend({ fresh: uvFresh || newInPipeline }, ensureInBag(uv));
            case 'geometry':
                // alltogether
                const { packed } = geom, geomFresh = freshness.packed;
                freshness.packed = false;
                return Utils.extend(ensureInBag(packed), { fresh: geomFresh || newInPipeline });
            default:
                throwBufferRequestError(key, GeometryBuffer);
        }
    }
    texture(_) {
        throw new Error("Method not implemented.");
    }
    _rebuildGeometryBuffers({ device }) {
        this._destroyBuffers();
        const version = performance.now();
        const geometry = this.item, key = geometry.key;
        if (isMeshGeometry(geometry)) {
            const meshGeometry = geometry;
            // flatten positions...
            const hasTriangleIndices = !Utils.isNullOrEmpty(meshGeometry.triangleIndices);
            const flatPositions = hasTriangleIndices ?
                meshGeometry.triangleIndices.map(index => meshGeometry.positions[index])
                : meshGeometry.positions;
            const length2 = flatPositions.length * 2, length3 = length2 + flatPositions.length;
            const positionsValue = new Float32Array(length3);
            const normalsValue = new Float32Array(length3);
            const uvValue = new Float32Array(length2);
            const geomValue = new Float32Array(2 * length3 + length2);
            for (let j = 0; j < flatPositions.length; j++) {
                const offset2 = 2 * j;
                const offset3 = 3 * j;
                const { x, y, z } = flatPositions[j];
                const { x: nx, y: ny, z: nz } = meshGeometry.normals[j];
                const { x: u, y: v } = meshGeometry.textureCoordinates[j];
                positionsValue.set([x, y, z], offset3);
                normalsValue.set([nx, ny, nz], offset3);
                uvValue.set([u, v], offset2);
                geomValue.set([x, y, z, nx, ny, nz, u, v], offset3 * 2 + offset2);
            }
            const positions = Buffers.create(device, positionsValue, LABEL_PREFIX + 'meshgeometry-' + key + '-positions-buffer');
            const normals = Buffers.create(device, normalsValue, LABEL_PREFIX + 'meshgeometry-' + key + '-normals-buffer');
            const uv = Buffers.create(device, uvValue, LABEL_PREFIX + 'meshgeometry-' + key + '-uvmap-buffer');
            const geom = Buffers.create(device, geomValue, LABEL_PREFIX + 'meshgeometry-' + key + '-buffer');
            this.#geometryBuffers = {
                packed: { buffer: geom, value: geomValue, version },
                positions: { buffer: positions, value: positionsValue, version },
                normals: { buffer: normals, value: normalsValue, version },
                uv: { buffer: uv, value: uvValue, version },
            };
            this.#freshness = { positions: true, normals: true, uv: true, packed: true };
        }
        else {
            const positionsValue = new Float32Array(AdapterUtils.flattenVectorArray(geometry.positions));
            const positions = Buffers.createStorage(device, positionsValue, LABEL_PREFIX + 'nodegeometry-' + key + '-positions-buffer');
            this.#geometryBuffers = {
                positions: { buffer: positions, value: positionsValue, version },
                packed: { buffer: positions, value: positionsValue, version }
            };
            this.#freshness = { positions: true, packed: true };
        }
    }
    _destroyBuffers() {
        this.#bufferBag = {};
        const obsolete = this.#geometryBuffers;
        if (!Utils.isNull(obsolete)) {
            this.destroySafely(obsolete.positions, obsolete.normals, obsolete.uv);
        }
    }
    dispose() {
        this._destroyBuffers();
    }
}
// #endregion
// #region MATERIAL
class MaterialBuffer extends ManagedBufferBase {
    #bufferBag = {};
    #textureBag = {};
    #wgpuMaterial;
    #buffers = new Map();
    #textures = new Map();
    constructor(material) {
        super(material);
        material.flags ??= [];
        if (material.flags.indexOf(StalePropertyFlag.Material) === -1) {
            material.flags.push(StalePropertyFlag.Material);
        }
    }
    buffer(context, key) {
        const flags = this.item.flags ?? [];
        let fresh = Utils.isNull(this.#wgpuMaterial);
        if (flags.includes(StalePropertyFlag.Material)) {
            this.#bufferBag = /* reset */ {};
            flags.splice(0);
            fresh = true;
            this.#textures.clear();
            this._resetMaterialBuffers();
        }
        const { pipeline } = context;
        const bag = (this.#bufferBag[key] ??= new Map());
        switch (key) {
            case 'material':
            case 'line':
                const buffer = this._ensureMaterialBuffer(context, key);
                if (buffer) {
                    if (!bag.has(pipeline)) {
                        fresh = true;
                        bag.set(pipeline, buffer);
                    }
                    return Utils.extend(buffer, { fresh });
                }
                return null;
            default:
                throwBufferRequestError(key, MaterialBuffer);
        }
    }
    _ensureMaterialBuffer(ctx, key = 'material') {
        const set = this.#buffers;
        let retval = set.get(key);
        if (retval) {
            return retval;
        }
        if (set.has(key)) {
            return null;
        }
        retval = this.#wgpuMaterial?.buffer(ctx, key);
        if (retval) {
            set.set(key, retval);
        }
        return retval;
    }
    _resetMaterialBuffers() {
        this.#wgpuMaterial = Materials.create(this.item);
        this._destroyBuffers();
    }
    texture(ctx, key = 'texture') {
        const set = this.#textures;
        let retval = set.get(key), fresh = false;
        if (!retval) {
            retval = this.#wgpuMaterial?.texture(ctx, key);
            if (!retval) {
                return null;
            }
            set.set(key, retval);
            fresh = true;
            //
            this.#textureBag = /* reset */ {};
        }
        const { pipeline } = ctx;
        const bag = (this.#textureBag[key] ??= new Map());
        if (!bag.has(pipeline)) {
            fresh = true;
            bag.set(pipeline, retval);
        }
        return Utils.extend(retval, { fresh });
    }
    dispose() {
        this._destroyBuffers();
    }
    _destroyBuffers() {
        // memoizers
        this.#bufferBag = {};
        this.#textureBag = {};
        // buffers
        const buffers = this.#buffers.values();
        this.#buffers.clear(); // clear before destroying
        this.destroySafely(...buffers);
        // textures
        const textures = this.#textures.values();
        this.#textures.clear(); // clear before destroying
        textures.forEach(t => t.texture.destroy());
    }
}
// #endregion
// #region INTERACTION
class InteractionBuffer extends ManagedBufferBase {
    #fields = {
        time: 0, cursor: { x: 0, y: 0 }, resolution: { width: 0, height: 0 }
    };
    #buffer;
    #bufferBag = new Map();
    constructor(data) {
        super(data);
        Utils.extend(this.#fields, data);
    }
    buffer(context, key) {
        switch (key) {
            // altogether
            case 'interaction':
                return this._ensureInteractionBuffer(context);
            default:
                throwBufferRequestError(key, InteractionBuffer);
        }
    }
    texture(_) {
        throw new Error("Method not implemented.");
    }
    _ensureInteractionBuffer(ctx, key = 'interaction') {
        const fields = this.#fields, item = this.item;
        let fresh = (!this.#buffer
            || item.time !== fields.time
            || item.resolution?.width !== fields.resolution.width
            || item.resolution?.height !== fields.resolution.height);
        if (!fresh && !Utils.isNull(item.cursor)) {
            fresh = item.cursor.x !== fields.cursor.x
                || item.cursor.y !== fields.cursor.y;
        }
        if (fresh) {
            // [4bytes cursor.x, 4bytes cursor.y, 4bytes resolution.width, 4bytes resolution.height, 4bytes time] = 20 bytes total]
            const value = new Float32Array([item.cursor?.x ?? 0, item.cursor?.y ?? 0, item.resolution.width, item.resolution.height, item.time, /* padding */ .0]);
            const buffer = Buffers.createUniform(ctx.device, value, LABEL_PREFIX + 'interaction-buffer');
            this.#buffer = { buffer, value, version: performance.now() };
            this.#bufferBag.clear();
        }
        const { pipeline } = ctx;
        const bag = this.#bufferBag;
        if (!bag.has(pipeline)) {
            fresh = true;
            bag.set(pipeline, this.#buffer);
        }
        return Utils.extend(this.#buffer, { fresh });
    }
    _destroyBuffers() {
        // memoizer
        this.#bufferBag.clear();
        // buffer itself
        const obsolete = this.#buffer;
        if (!Utils.isNull(obsolete)) {
            this.destroySafely(obsolete);
        }
    }
    dispose() {
        this._destroyBuffers();
    }
}
// #endregion
// #region MESH
class RenderableMeshBuffer extends RenderableBufferBase {
    #geometryBuffers;
    #geometryBuffer;
    #materialBuffers;
    #materialBuffer;
    #worldTransform;
    #freshness = {
        geometry: true, material: true, transform: true
    };
    #worldTransformBufferBag = new Map();
    constructor(mesh, parent, geometries, materials) {
        super(mesh, parent);
        mesh.flags ??= [];
        if (mesh.flags.indexOf(StalePropertyFlag.Geometry) === -1) {
            mesh.flags.push(StalePropertyFlag.Geometry);
        }
        if (mesh.flags.indexOf(StalePropertyFlag.Material) === -1) {
            mesh.flags.push(StalePropertyFlag.Material);
        }
        if (mesh.flags.indexOf(StalePropertyFlag.Position) === -1 && mesh.flags.indexOf(StalePropertyFlag.Transform) === -1) {
            mesh.flags.push(StalePropertyFlag.Transform);
        }
        this.#geometryBuffers = geometries;
        this.#materialBuffers = materials;
    }
    texture(ctx, key) {
        const material = this.#materialBuffer;
        return material?.texture(ctx, key);
    }
    specificBuffer(context, key) {
        const freshness = this.#freshness;
        const flags = this.allFlags();
        if (Utils.isNull(this.#worldTransform) || flags.includes(StalePropertyFlag.Position) || flags.includes(StalePropertyFlag.Transform)) {
            this.#worldTransformBufferBag.clear();
            // world transform bind group buffer
            freshness.transform = true;
            this._rebuildWorldMatrices(context);
        }
        if (Utils.isNull(this.#geometryBuffer) || flags.includes(StalePropertyFlag.Geometry)) {
            // vertex buffers
            freshness.geometry = true;
            this._ensureGeometryBuffer();
        }
        if (Utils.isNull(this.#materialBuffer) || flags.includes(StalePropertyFlag.Material)) {
            freshness.material = true;
            this._ensureMaterialBuffer();
        }
        RenderableBuffers.clearFlags(this);
        const geom = this.#geometryBuffer;
        const mater = this.#materialBuffer;
        switch (key) {
            case 'geometryPositions':
            case 'geometryNormals':
            case 'geometryTexCoords':
            case 'geometry':
                return geom.buffer(context, key);
            case 'material':
            case 'line':
                return mater.buffer(context, key);
            case 'objectWorldTransform':
                const mat = this.#worldTransform, wFresh = freshness.transform;
                freshness.transform = false;
                const bag = this.#worldTransformBufferBag, { pipeline } = context;
                const newInPipeline = !bag.has(pipeline);
                if (newInPipeline) {
                    bag.set(pipeline, mat);
                }
                return Utils.extend(mat, { fresh: wFresh || newInPipeline });
            default:
                throwBufferRequestError(key, RenderableMeshBuffer);
        }
    }
    _ensureGeometryBuffer() {
        const current = this.#geometryBuffer;
        const cache = this.#geometryBuffers;
        const mesh = this.item;
        const geom = mesh.geometry;
        // no cache provided? destroy and rebuild
        if (Utils.isNull(cache)) {
            current?.destroy();
            this.#geometryBuffer = new GeometryBuffer(geom);
        }
        else {
            let buffer = cache?.get(geom);
            if (buffer?.disposed !== false) {
                buffer = new GeometryBuffer(geom);
                cache.set(geom, buffer);
            }
            this.#geometryBuffer = buffer;
        }
    }
    _ensureMaterialBuffer() {
        const current = this.#materialBuffer;
        const cache = this.#materialBuffers;
        const mesh = this.item;
        const material = mesh.material ?? DefaultMaterial;
        // no cache provided? destroy and rebuild
        if (Utils.isNull(cache)) {
            current?.destroy();
            this.#materialBuffer = new MaterialBuffer(material);
        }
        else {
            let buffer = cache?.get(material);
            if (buffer?.disposed !== false) {
                buffer = new MaterialBuffer(material);
                cache.set(material, buffer);
            }
            this.#materialBuffer = buffer;
        }
    }
    _rebuildWorldMatrices({ device }) {
        //this.destroySafely(this.#worldTransform);
        const item = this.item;
        const matrix = this.buildWorldTransform();
        const normal = Matrix3D.transpose(Matrix3D.invert(matrix));
        const value = new Float32Array(28);
        value.set(Matrix3D.toArray(matrix), 0);
        value.set([normal.m11, normal.m12, normal.m13, 0, normal.m21, normal.m22, normal.m23, 0, normal.m31, normal.m32, normal.m33, 0], 16);
        //const current = this.#worldTransform;
        // reuse GPUBuffer (always same size)
        if (Utils.isNull(this.#worldTransform)) {
            const buffer = Buffers.createUniform(device, value, LABEL_PREFIX + 'mesh-' + item.key + '-worldmatrix-buffer');
            this.#worldTransform = { buffer, value, version: performance.now().roundoff() };
        }
        else {
            this.#worldTransform.value = value;
            this.#worldTransform.version = performance.now().roundoff();
        }
        //if (!Utils.isNull(current)) {
        //    this.destroySafely(current);
        //}
    }
    dispose() {
        // memoizer
        this.#worldTransformBufferBag.clear();
        const world = this.#worldTransform;
        this.#worldTransform = null;
        this.destroySafely(world);
        const material = this.#materialBuffer;
        this.#materialBuffer = null;
        material?.destroy();
        const geometry = this.#geometryBuffer;
        this.#geometryBuffer = null;
        geometry?.destroy();
    }
}
// #endregion
// #region LIGHTS
class RenderableLightBuffer extends RenderableBufferBase {
    #buffer;
    #bufferBag = new Map();
    constructor(light) {
        super(light);
        light.flags.push(StalePropertyFlag.Light);
    }
    /**
     * Ambient light buffer building
     * @param param0
     */
    _rebuildAmbientLightBuffer({ device }) {
        const light = this.item;
        // sizes/offsets
        const szColor = 4, szIntensity = 1, offsetColor = 0, offsetIntensity = 4;
        const szTotal = 24; // offsetIntensity + szIntensity;
        const values = new Float32Array(szTotal);
        // color
        const { r, g, b, a } = Colors.parse(light.color ?? '#fff');
        values.set([r, g, b, a], offsetColor);
        // intensity
        values.set([light.intensity], offsetIntensity);
        this.#buffer = { value: values, buffer: Buffers.createUniform(device, values, LABEL_PREFIX + 'ambient-light-buffer'), version: performance.now() };
    }
    /**
     * Directional light buffer building
     * @param param0
     */
    _rebuildDirectionalLightBuffer({ device }) {
        const light = this.item;
        // sizes/offsets
        const szIntensity = 1, szColor = 4, szDirection = 3, offsetColor = 0, offsetDirection = 4, // see: https://www.w3.org/TR/WGSL/#memory-layouts
        offsetIntensity = 7;
        const szTotal = offsetIntensity + szIntensity;
        const values = new Float32Array(szTotal);
        // color
        const { r, g, b, a } = Colors.parse(light.color ?? '#fff');
        values.set([r, g, b, a], offsetColor);
        // intensity
        values.set([light.intensity], offsetIntensity);
        // direction
        const direction = Vector3D.subtract(light.position, light.target);
        values.set([direction.x, direction.y, direction.z], offsetDirection);
        this.#buffer = { value: values, buffer: Buffers.createUniform(device, values, LABEL_PREFIX + 'directional-light-buffer'), version: performance.now() };
    }
    /**
     * Omni light buffer building
     * @param param0
     */
    _rebuildOmniLightBuffer({ device }) {
        const light = this.item;
        // sizes/offsets
        const szIntensity = 1, szColor = 4, szPosition = 3, offsetColor = 0, offsetPosition = 4, // see: https://www.w3.org/TR/WGSL/#memory-layouts
        offsetIntensity = 7;
        const szTotal = offsetIntensity + szIntensity;
        const values = new Float32Array(szTotal);
        // color
        const { r, g, b, a } = Colors.parse(light.color ?? '#fff');
        values.set([r, g, b, a], offsetColor);
        // intensity
        values.set([light.intensity], offsetIntensity);
        // position
        values.set([light.position.x, light.position.y, light.position.z], offsetPosition);
        this.#buffer = { value: values, buffer: Buffers.createUniform(device, values, LABEL_PREFIX + 'omni-light-buffer'), version: performance.now() };
    }
    _rebuildLight(context) {
        this.destroySafely(this.#buffer);
        const light = this.item;
        switch (light.type) {
            case 'direction':
                this._rebuildDirectionalLightBuffer(context);
                break;
            case 'omni':
                this._rebuildOmniLightBuffer(context);
                break;
            case 'ambient':
                this._rebuildAmbientLightBuffer(context);
                break;
            default:
                throw new Error(`Light '${light.type}' is not supported.`);
        }
    }
    texture(_) {
        throw new Error("Method not implemented.");
    }
    specificBuffer(context, key) {
        const bag = this.#bufferBag;
        let fresh = Utils.isNull(this.#buffer) || this.allFlags().length > 0;
        if (fresh) {
            bag.clear();
            this._rebuildLight(context);
        }
        RenderableBuffers.clearFlags(this);
        const { pipeline } = context;
        if (!bag.has(pipeline)) {
            fresh = true;
            bag.set(pipeline, this.#buffer);
        }
        if (key === 'light') {
            return Utils.extend(this.#buffer, { fresh });
        }
        throwBufferRequestError(key, RenderableLightBuffer);
    }
    dispose() {
        this.#bufferBag.clear();
        const buffer = this.#buffer;
        this.#buffer = null;
        this.destroySafely(buffer);
    }
}
// #endregion
